import { ClipboardCheck } from "lucide-react";
import * as React from "react";

import { VideoProject } from "@/services/bedrock";

import { FlaggedIssueCard } from "./FlaggedIssueCard";
import { lastFailComment, lastFailTimestampMs, LastFailResolution, QaFlag } from "./qaIssues";
import { HudCard } from "./studioChrome";

export function QaFlaggedPanel({
  displayedPriorIssues,
  flags,
  viewingPast,
  studioReadOnly,
  canFlag,
  lastFailIssues,
  lastFailResolutions,
  priorTimestampOverrides,
  priorCommentOverrides,
  durationSeconds,
  selectedFlagId,
  selectedLastFailId,
  highlightUnmarkedLastFail,
  firstUnmarkedLastFailId,
  firstUnmarkedLastFailRef,
  autoFocusComment,
  onSelectLastFail,
  onSelectFlag,
  onChangeLastFailTimestamp,
  onChangeFlagTimestamp,
  onUsePlayheadLastFail,
  onUsePlayheadFlag,
  onChangeLastFailComment,
  onChangeFlagComment,
  onDeselectLastFail,
  onDeselectFlag,
  onResolved,
  onUnresolved,
  onDeleteFlag,
}: {
  displayedPriorIssues: VideoProject.QaAuditIssue[];
  flags: QaFlag[];
  viewingPast: boolean;
  studioReadOnly: boolean;
  canFlag: boolean;
  lastFailIssues: VideoProject.QaAuditIssue[];
  lastFailResolutions: Record<number, LastFailResolution | undefined>;
  priorTimestampOverrides: Record<number, number | null | undefined>;
  priorCommentOverrides: Record<number, string | undefined>;
  durationSeconds: number;
  selectedFlagId: string | null;
  selectedLastFailId: number | null;
  highlightUnmarkedLastFail: boolean;
  firstUnmarkedLastFailId?: number;
  firstUnmarkedLastFailRef: React.Ref<HTMLDivElement>;
  autoFocusComment?: boolean;
  onSelectLastFail: (issue: VideoProject.QaAuditIssue, timestampMs: number | null) => void;
  onSelectFlag: (flag: QaFlag) => void;
  onChangeLastFailTimestamp: (id: number, timestampMs: number | null) => void;
  onChangeFlagTimestamp: (id: string, timestampMs: number | null) => void;
  onUsePlayheadLastFail: (id: number) => void;
  onUsePlayheadFlag: (id: string) => void;
  onChangeLastFailComment: (id: number, comment: string) => void;
  onChangeFlagComment: (id: string, comment: string) => void;
  onDeselectLastFail: () => void;
  onDeselectFlag: () => void;
  onResolved: (id: number) => void;
  onUnresolved: (id: number) => void;
  onDeleteFlag: (id: string) => void;
}) {
  return (
    <HudCard title="Flagged" icon={ClipboardCheck} iconClass="text-amber-600">
      {displayedPriorIssues.map(issue => {
        const timestampMs = lastFailTimestampMs(issue, priorTimestampOverrides);
        const comment = lastFailComment(issue, priorCommentOverrides);
        const focused = selectedLastFailId === issue.id;
        const needsResolution = !viewingPast && highlightUnmarkedLastFail && lastFailResolutions[issue.id] == null;
        return (
          <FlaggedIssueCard
            key={`last-fail-${issue.id}`}
            issueId={`last-fail-${issue.id}`}
            label={VideoProject.qaIssueCategoryLabel(issue.category)}
            comment={comment}
            timestampMs={timestampMs}
            durationSeconds={durationSeconds}
            focused={focused}
            needsResolution={needsResolution}
            cardRef={needsResolution && issue.id === firstUnmarkedLastFailId ? firstUnmarkedLastFailRef : undefined}
            readOnly={studioReadOnly}
            resolution={viewingPast ? undefined : lastFailResolutions[issue.id]}
            autoFocusComment={focused && autoFocusComment}
            onSelect={() => onSelectLastFail(issue, timestampMs)}
            onChangeTimestamp={nextMs => onChangeLastFailTimestamp(issue.id, nextMs)}
            onUsePlayhead={() => onUsePlayheadLastFail(issue.id)}
            onChangeComment={nextComment => onChangeLastFailComment(issue.id, nextComment)}
            onDeselect={onDeselectLastFail}
            onResolved={viewingPast ? undefined : () => onResolved(issue.id)}
            onUnresolved={viewingPast ? undefined : () => onUnresolved(issue.id)}
          />
        );
      })}
      {viewingPast
        ? null
        : flags.map(flag => {
            const focused = selectedFlagId === flag.id;
            return (
              <FlaggedIssueCard
                key={flag.id}
                issueId={`flag-${flag.id}`}
                label={VideoProject.qaIssueCategoryLabel(flag.category)}
                comment={flag.comment}
                timestampMs={flag.timestampMs}
                durationSeconds={durationSeconds}
                focused={focused}
                readOnly={studioReadOnly}
                canDelete={!studioReadOnly}
                autoFocusComment={focused && autoFocusComment}
                onSelect={() => onSelectFlag(flag)}
                onChangeTimestamp={nextMs => onChangeFlagTimestamp(flag.id, nextMs)}
                onUsePlayhead={() => onUsePlayheadFlag(flag.id)}
                onChangeComment={nextComment => onChangeFlagComment(flag.id, nextComment)}
                onDeselect={onDeselectFlag}
                onDelete={() => onDeleteFlag(flag.id)}
              />
            );
          })}
      {studioReadOnly ? (
        !displayedPriorIssues.length && (viewingPast || !flags.length) ? (
          <p className="text-xs text-slate-400">
            {viewingPast ? "No QA issues on this audit." : "No QA issues on this project."}
          </p>
        ) : null
      ) : !canFlag ? (
        <p className="text-xs text-slate-400">Switch to the edited video to flag issues.</p>
      ) : !lastFailIssues.length && !flags.length ? (
        <p className="text-xs text-slate-400">Press 1–5 to drop a pin at the playhead.</p>
      ) : !flags.length ? (
        <p className="text-xs text-slate-400">Press 1–5 to flag a new issue.</p>
      ) : null}
    </HudCard>
  );
}
